import * as React from 'react';
import { Component } from 'react';
import { AjaxWrapper, HttpMethod } from '../../generic/ajaxWrapper';
import { BettyBot } from '../betty-bot/component';
import { RasaContext } from '../../context';
import { SharedKeys, SharedStore } from '../../shared/data-layer/sharedStore';

interface BettyConfig {
  apiUrl: string;
  apiEndpointToken: string;
  stream?: boolean;
  userId?: string;
  infoHtml?: string;
  assistantImage?: string;
  userImage?: string;
  termsUrl?: string;
  lowerHtml?: string;
  displayConversationList?: boolean;
  displayKnowledgePlus?: boolean;
  inputPlaceholder?: string;
  disableDownload?: boolean;
  referencesSectionTitle?: string;
  referencesSectionCollapsed?: number;
  logoUrl?: string;
  welcomeTitle?: string;
  staticQuestions?: string[];
}

interface PartnerConfig {
  SCRIPTS?: { id: string; src: string; }[];
  CHAT_TYPE?: 'betty' | 'hubspot';
  BETTY_CONFIG?: BettyConfig;
  [key: string]: any;
}

interface Props {
  children: any;
}

interface State {
  showBetty: boolean;
  bettyConfig?: BettyConfig;
  currentUserId?: string;
}
export class ScriptLoader extends Component<Props, State> {
  public static contextType = RasaContext;
  private sharedStore: SharedStore;

  constructor(props: Props) {
    super(props);
    this.state = {
      showBetty: false,
      bettyConfig: undefined,
      currentUserId: undefined,
    };
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context);
    
    const currentDomain = window.location.hostname;
    const url: string = AjaxWrapper.getServerUrl() + `/public/partner-config/${currentDomain}`;
    
    AjaxWrapper.ajax(url, HttpMethod.GET, null).then((configs: PartnerConfig) => {
      const chatType = configs.CHAT_TYPE;
      
      if (chatType === 'betty' && configs.BETTY_CONFIG) {
        this.loadBettyWidget(configs.BETTY_CONFIG);
      } else {
        this.loadHubSpotScripts(configs.SCRIPTS || []);
      }
    }).catch((error) => {
      this.loadHubSpotScripts([]);
    });
  }

  private loadBettyWidget(bettyConfig: BettyConfig) {
    this.loadBettyAssets().then(() => {
      this.getUserEmail().then((userEmail) => {
        this.setState({
          showBetty: true,
          bettyConfig,
          currentUserId: userEmail,
        });
      });
    }).catch((error) => {
      this.loadHubSpotScripts([]);
    });
  }

  private loadBettyAssets(): Promise<void> {
    return new Promise((resolve, reject) => {
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = 'https://files.bettybot.ai/ui-widget/betty-bot-ui.css';
      cssLink.id = 'betty-css';
      
      const script = document.createElement('script');
      script.src = 'https://files.bettybot.ai/ui-widget/betty-bot-ui.js';
      script.id = 'betty-script';
      script.async = true;
      script.defer = true;

      let loadedCount = 0;
      const onLoad = () => {
        loadedCount++;
        if (loadedCount === 2) {
          resolve();
        }
      };

      const onError = () => {
        reject(new Error('Failed to load Betty assets'));
      };

      cssLink.onload = onLoad;
      cssLink.onerror = onError;
      script.onload = onLoad;
      script.onerror = onError;

      if (!document.getElementById('betty-css')) {
        document.head.appendChild(cssLink);
      } else {
        onLoad();
      }

      if (!document.getElementById('betty-script')) {
        document.head.appendChild(script);
      } else {
        onLoad();
      }
    });
  }

  private loadHubSpotScripts(scripts: { id: string; src: string; }[]) {
    scripts.forEach(({ id, src }) => {
      if (!document.getElementById(id)) {
        const script = document.createElement("script");
        script.id = id;
        script.src = src;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    });
  }

  private async getUserEmail(): Promise<string> {
    try {
      const person = await this.sharedStore.getValue(SharedKeys.person);
      return (person && person.email) || "Anonymous";
    } catch (error) {
      return "Anonymous";
    }
  }
  public render() {
    const { showBetty, bettyConfig, currentUserId } = this.state;
    
    return (
      <>
        {this.props.children}
        {showBetty && bettyConfig && (
          <BettyBot config={bettyConfig} userId={currentUserId} />
        )}
      </>
    );
  }
}
